import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PayrollService } from '../../../services/payroll.service';
import { ClientSetup } from '../../../models/client';
import { MasterPayrollObject, PayrollStartData, PayTypes } from '../../../models/payroll';
import {
  CompanyLocationConfig,
  PayrollCalendarSetup
} from '../../../models/company-location-config';
import { CompaniesService } from '../../../services/companies.service';
import { concatMap, map, takeUntil, tap } from 'rxjs/operators';
import { PayrollManagement } from '../../../tools/payroll-management';
import { AclService } from '../../../services/acl.service';
import { Observable, of, Subject } from 'rxjs';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';
import { Store } from '@ngxs/store';
import { ClientFeatureWhitelistService } from '../../../services/client-feature-whitelist.service';

@Component({
  selector: 'payroll-landing-page',
  templateUrl: './payroll-landing-page.component.html',
  styleUrls: ['./payroll-landing-page.component.css'],
  standalone: false
})
export class PayrollLandingPageComponent implements OnInit {
  public clientCode: string;
  public clientSetup: ClientSetup;
  public location: CompanyLocationConfig;
  public startData: PayrollStartData;
  public prefundWarnings: string[];
  public isCsr = false;
  public isAutoPayrollClient: boolean = false;
  public activeAutoPayGrid: MasterPayrollObject;
  public canAccessGeneralLedgerTile: boolean = false;

  private destroy$ = new Subject<void>();

  get payTypes() {
    return PayTypes;
  }

  constructor(
    private companyService: CompaniesService,
    private route: ActivatedRoute,
    private payrollService: PayrollService,
    private payrollMgmt: PayrollManagement,
    private aclService: AclService,
    private store: Store,
    private whitelistService: ClientFeatureWhitelistService
  ) {}

  ngOnInit(): void {
    this.clientCode = this.route.snapshot.paramMap.get('client_code');
    this.aclService.CurrentUserHasMinimumRole('CSR1').subscribe(result => {
      this.isCsr = result;
    });
    this._getStartAndSetupData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  //#region Pubic Methods

  //#endregion

  //#region Private Methods

  private _getStartAndSetupData() {
    this.companyService
      .getClientSetup(this.clientCode)
      .pipe(
        concatMap(setup => {
          this.clientSetup = setup;

          return this.payrollService.getPayrollStartData(this.clientCode).pipe(
            map(payrollStartData => {
              this.startData = payrollStartData;
            })
          );
        })
      )
      .pipe(
        concatMap(_ => {
          if (
            this.startData.ActivePayroll != null &&
            this.startData.ActivePayroll.PayType != PayTypes.Manual
          ) {
            // create paydate info object by merging start paydate info object with data from active payroll
            const locationInfo = this.startData.Locations[this.startData.ActivePayroll.LocationId];
            if (locationInfo == null || locationInfo.NextPayDates.length == 0) {
              return of({});
            }

            return this.payrollService
              .getPrefundWarnings(
                {
                  ...locationInfo.NextPayDates[0],
                  ...{
                    TargetPayDate: this.startData.ActivePayroll.PayDate,
                    PeriodEnd: this.startData.ActivePayroll.Calendars[0].PeriodEnd
                  }
                },
                this.startData.ActivePayroll.Calendars.map(x => x.CalendarId),
                this.startData.ActivePayroll.PayType
              )
              .pipe(map(warnings => (this.prefundWarnings = warnings)));
          }

          //Don't check prefund warnings if we don't already have a payroll started
          return of({});
        })
      )
      .pipe(
        concatMap(_ => {
          return this.store
            .select(ClientSelectionState.locations)
            .pipe(takeUntil(this.destroy$))
            .pipe(
              tap(locationConfigs => {
                if (locationConfigs && locationConfigs.length) {
                  this.isAutoPayrollClient =
                    this.payrollMgmt.isClientSetupForAutoPayroll(locationConfigs);

                  this.location = locationConfigs[0];
                }
              })
            );
        })
      )
      .subscribe(() => {
        if (this.isCsr && this.clientSetup.HasGeneralLedger) {
          this.canAccessGeneralLedgerTile = true;
        }
        if (!this.isCsr && this.clientSetup.HasGeneralLedger) {
          //do a whitelist check
          this.whitelistService
            .checkWhitelistForFeature(this.clientCode, 'generalLedger')
            .subscribe(result => {
              this.canAccessGeneralLedgerTile = result;
            });
        }

        if (this.isAutoPayrollClient) {
          const calId = this.location.Calendars.map(x => x.CalendarId)[0];
          // check if an automatic payroll is in progress
          this.payrollService
            .getNextPayDate(this.clientCode, this.location.Id, calId)
            .subscribe(pay => {
              this.payrollMgmt
                .findMatchingWorksheet(this.clientCode, this.location.Id, calId, pay.TargetPayDate)
                .subscribe(grid => {
                  this.activeAutoPayGrid = grid;
                });
            });
        }
      });
  }

  //#endregion
}
